import { ManageColumnsModal } from "@/components/column-manager/ManageColumnsModal.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnConfig, ColumnDefinition } from "@/types/column.types.ts";
import { Button, useModals, FontAwesomeIcon } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export function useColumns<T, C extends string = string>({
  columnConfig,
  defaultVisibleColumns,
  columnDefinitions,
  seedColumns,
  seedColumnsKey,
  source,
}: {
  columnConfig: ColumnConfig;
  defaultVisibleColumns: C[];
  columnDefinitions: Record<C, ColumnDef<T>>;
  seedColumns?: ColumnDefinition[];
  seedColumnsKey?: string | number;
  source?: string;
}) {
  const { t } = useTranslation();
  const [visibleColumns, setVisibleColumns] = useState<C[]>(
    seedColumns ? seedColumns.map((col) => col.id as C) : defaultVisibleColumns
  );
  const modalsService = useModals();

  const [syncedSeedColumnsKey, setSyncedSeedColumnsKey] = useState(seedColumnsKey);
  if (!Object.is(seedColumnsKey, syncedSeedColumnsKey)) {
    setSyncedSeedColumnsKey(seedColumnsKey);
    setVisibleColumns(seedColumns ? seedColumns.map((col) => col.id as C) : defaultVisibleColumns);
  }

  const fixedColumnIds = useMemo(
    () =>
      columnConfig.categories.flatMap((category) =>
        category.columns.filter((col) => col.isFixed).map((col) => col.id as C)
      ),
    [columnConfig]
  );

  const allColumnDefinitions = useMemo(
    () => columnConfig.categories.flatMap((category) => category.columns),
    [columnConfig]
  );

  const columns = useMemo(() => {
    const fixedCols = fixedColumnIds.map((id) => columnDefinitions[id]).filter(Boolean);
    const nonFixedCols = visibleColumns
      .filter((id) => !fixedColumnIds.includes(id))
      .map((id) => columnDefinitions[id])
      .filter(Boolean);

    return [...fixedCols, ...nonFixedCols];
  }, [visibleColumns, columnDefinitions, fixedColumnIds]);

  const currentColumns = useMemo(
    () =>
      visibleColumns
        .filter((id) => !fixedColumnIds.includes(id))
        .map((id) => allColumnDefinitions.find((col) => col.id === id))
        .filter((col): col is ColumnDefinition => Boolean(col)),
    [visibleColumns, fixedColumnIds, allColumnDefinitions]
  );

  const modalColumnConfig = useMemo(
    () => ({
      ...columnConfig,
      categories: columnConfig.categories
        .map((category) => ({
          ...category,
          columns: category.columns.filter((col) => !col.isFixed),
        }))
        .filter((category) => category.columns.length > 0),
    }),
    [columnConfig]
  );

  const handleManageColumns = useCallback(() => {
    modalsService.openModal(ManageColumnsModal, {
      columnConfig: modalColumnConfig,
      visibleColumns: visibleColumns.filter((id) => !fixedColumnIds.includes(id)),
      onSubmit: (updatedColumnDefinitions: ColumnDefinition[]) => {
        setVisibleColumns([
          ...fixedColumnIds,
          ...updatedColumnDefinitions.map((col) => col.id as C),
        ]);
        analyticsService.trackEvent(
          analyticEvents.employees.settingsEdited,
          source ? { [analyticProperties.source]: source } : {}
        );
      },
    });
  }, [modalColumnConfig, visibleColumns, fixedColumnIds, source, modalsService]);

  const columnsButton = () => {
    return (
      <Button
        onClick={handleManageColumns}
        leftIcon={<FontAwesomeIcon icon="fa-light fa-line-columns" />}
        variant="secondary"
      >
        {t("common:columns")}
      </Button>
    );
  };

  return {
    columns,
    visibleColumns,
    currentColumns,
    handleManageColumns,
    columnsButton,
  };
}
